import { MEDIA_QUERY, useResponsive } from '@bucketplace/design-system';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { type ComponentPropsWithoutRef, forwardRef, useMemo } from 'react';

import { useRollingTextIndex } from '@/brand/hooks';

import { DOCUMENTARY_SIZE } from '../../constants';

const ReactTextTransition = dynamic(() => import('react-text-transition'), {
  ssr: false,
});

type DocumentaryTextProps = ComponentPropsWithoutRef<'p'>;

/** 미니 다큐 텍스트 */
export const DocumentaryText = forwardRef<
  HTMLParagraphElement,
  DocumentaryTextProps
>((props, ref) => {
  return <StyledDocumentaryText ref={ref} {...props} />;
});

interface DocumentaryRollingTextProps {
  scrollProgress?: number;
  before?: string;
  after?: string;
  rollingText: string[];
  innerRef?: React.RefObject<HTMLSpanElement>;
}

export const DocumentaryRollingText = forwardRef<
  HTMLParagraphElement,
  DocumentaryRollingTextProps
>(({ before, after, rollingText, scrollProgress = 0, innerRef }, ref) => {
  const index = useRollingTextIndex(rollingText.length, scrollProgress);
  const { breakpoint } = useResponsive({});
  const breakpointKey = useMemo(
    () =>
      breakpoint === 'mobile' || breakpoint === 'tablet' ? 'mobile' : 'laptop',
    [breakpoint],
  );

  return (
    <StyledDocumentaryText ref={ref} role="status" aria-live="polite">
      {before}
      {rollingText.length > 0 && (
        <RollingTextWrapper ref={innerRef}>
          <ReactTextTransition key={breakpointKey} inline>
            {rollingText[index] ?? rollingText[0]}
          </ReactTextTransition>
        </RollingTextWrapper>
      )}
      {after}
    </StyledDocumentaryText>
  );
});

export const DocumentaryTextContainer = styled.div`
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: center;

  width: ${DOCUMENTARY_SIZE.mobile.width}px;
  height: ${DOCUMENTARY_SIZE.mobile.height}px;

  ${MEDIA_QUERY.min(
    'tablet',
    css`
    width: ${DOCUMENTARY_SIZE.tablet.width}px;
    height: ${DOCUMENTARY_SIZE.tablet.height}px;
  `,
  )}

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    width: ${DOCUMENTARY_SIZE.desktop.width}px;
    height: ${DOCUMENTARY_SIZE.desktop.height}px;
  `,
  )}
`;

const StyledDocumentaryText = styled.p`
  position: absolute;
  left: ${DOCUMENTARY_SIZE.mobile.left}px;
  opacity: 0;
  width: max-content;
  color: ${({ theme }) => theme.colors.foregroundInverse};

  font-size: ${DOCUMENTARY_SIZE.tablet.fontSize}px;
  font-weight: 600;
  line-height: 130%;
  letter-spacing: -0.48px;
  white-space: pre-line;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    left: ${DOCUMENTARY_SIZE.laptop.left}px;
    font-size: ${DOCUMENTARY_SIZE.desktop.fontSize}px;
  `,
  )}
`;

const RollingTextWrapper = styled.span`
  display: inline;
  opacity: 1;
`;
